import { get } from 'lodash';
import { PATH_DETAILS_METRIC_TARGET_VALUE } from '../../constant';
import { DAY_IN_MS, DEFAULT_TARGET, MIN_DAYS } from '../constants';
import { ChartPoint } from '../types';

export const getPerformanceAnalysisData = (
  breakdownGraph: any,
): ChartPoint[] => {
  const result = breakdownGraph?.body?.result;
  const achievements = result?.achievement || [];

  const startTime = new Date(result?.start_date || '').getTime();
  const endTime = new Date(result?.end_date || '').getTime();

  if (!Number.isFinite(startTime)) {
    return [];
  }

  const minEndTime = startTime + (MIN_DAYS - 1) * DAY_IN_MS;
  const resolvedEndTime = Number.isFinite(endTime)
    ? Math.max(endTime, minEndTime)
    : minEndTime;

  const valueByDate: Record<string, number> = {};
  achievements.forEach((achievement) => {
    const timestamp = new Date(achievement?.last_updated_date || '').getTime();
    if (!Number.isFinite(timestamp)) {
      return;
    }

    const dateKey = new Date(timestamp).toISOString().slice(0, 10);
    valueByDate[dateKey] = achievement?.value ?? 0;
  });

  const totalDays = Math.floor((resolvedEndTime - startTime) / DAY_IN_MS) + 1;

  const computedSeries = Array.from({ length: totalDays }, (_, index) => {
    const current = new Date(startTime + index * DAY_IN_MS);
    const dateKey = current.toISOString().slice(0, 10);
    const currentValue = valueByDate[dateKey];

    return {
      date: dateKey,
      value: currentValue,
      target: DEFAULT_TARGET,
    };
  });

  return [
    {
      date: '0',
      value: 0,
      target: 0,
    },
    ...computedSeries,
  ];
};

export const getTrendAnalysisData = (trendGraph: any): ChartPoint[] => {
  const groupedRecordSets = trendGraph?.body?.result?.grouped_record_sets || [];

  const parsedPoints = groupedRecordSets
    .map((recordSet) => {
      const timestamp = Number(recordSet?.group_by_key?.value);
      if (!Number.isFinite(timestamp)) {
        return null;
      }

      return {
        timestamp,
        value: recordSet?.metric?.value ?? null,
        target: recordSet?.metric?.target?.value ?? null,
      };
    })
    .filter(
      (
        point,
      ): point is {
        timestamp: number;
        value: number | null;
        target: number | null;
      } => Boolean(point),
    )
    .sort((a, b) => a.timestamp - b.timestamp);

  if (!parsedPoints.length) {
    return [];
  }

  const minTime = parsedPoints[0]?.timestamp;
  const maxTime = parsedPoints[parsedPoints.length - 1]?.timestamp;

  const valueByDate: Record<
    string,
    { value: number | null; target: number | null }
  > = {};
  parsedPoints.forEach((point) => {
    const dateKey = new Date(point.timestamp).toISOString().slice(0, 10);
    valueByDate[dateKey] = {
      value: point.value,
      target: point.target,
    };
  });

  const totalDays = Math.floor((maxTime - minTime) / DAY_IN_MS) + 1;

  return Array.from({ length: totalDays }, (_, index) => {
    const current = new Date(minTime + index * DAY_IN_MS);
    const dateKey = current.toISOString().slice(0, 10);
    const metricEntry = valueByDate[dateKey];

    return {
      date: dateKey,
      value: metricEntry?.value ?? null,
      target: metricEntry?.target ?? null,
    };
  });
};

export const getReferenceTargetInfo = (keyMetricsDetails: any) => {
  const expectedTarget = get(
    keyMetricsDetails,
    PATH_DETAILS_METRIC_TARGET_VALUE,
    '',
  );
  const referenceTarget = Number(expectedTarget);

  return {
    referenceTarget,
    hasReferenceTarget: Number.isFinite(referenceTarget),
  };
};
